/* @flow */

// Params

export const forStartupParam = 'for_startup';

// Login

export function loginEndpoint() {
  return (apiUrl: string) => `${apiUrl}login/`;
}

export function loginWithDoubleDutchEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/doubledutch/login/`;
}

export function loginWithExternalAuthEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/sso/login/`;
}

// Signup

export function signupActivationEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/signup/check_code/`;
}

export function signupEmailEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/signup/check_username/`;
}

export function signupCreateUserEndpoint() {
  return (apiUrl: string) => `${apiUrl}plugins/signup/create_user/`;
}

// Support

export function supportRetrieveUsernameEndpoint(eventId: number | string) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/retrieve_membership/`;
}

export function supportLoginLinkEndpoint(eventId: number | string) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/send_login_link/`;
}

export function supportContactEndpoint(evendId: number | string) {
  return (apiUrl: string) =>
    `${apiUrl}events/${evendId}/send_email_to_support/`;
}

// Users

export function usersEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/users/`;
}

// To be consistent with statistics, we pass the eventID
// to get the stats for the user only for this event
export function userEndpoint(id: number | string, eventId?: number | string) {
  if (eventId) {
    // $FlowIssue
    return (apiUrl: string) => `${apiUrl}users/${id}/?event=${eventId}`;
  }
  return (apiUrl: string) => `${apiUrl}users/${id}/`;
}

export function userStatisticsEndpoint(id: number, eventId: number) {
  return (apiUrl: string) =>
    `${apiUrl}users/${id}/statistics/?event=${eventId}`;
}

export function userCanCreateTopicEndpoint(
  id: number,
  eventId: number,
  kind: string,
) {
  return (apiUrl: string) =>
    `${apiUrl}users/${id}/can_create_topic/?event=${eventId}&kind=${kind}`;
}

export function userActivityEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${id}/unavailabilities/`;
}

export function userValidateEmailEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/check_email/`;
}

export function userSetPasswordEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/set_password/`;
}

export function userSendPasswordLinkEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/reset_password_init/`;
}

export function userResetPasswordEndpoint() {
  return (apiUrl: string) => `${apiUrl}users/reset_password/`;
}

export function userChangePasswordEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${id}/change_password/`;
}

export function userAvatarEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}users/${id}/avatar/`;
}

export function userTopicKeywordsEndpoint(
  userId: number | string,
  eventId: number,
) {
  return (apiUrl: string) =>
    `${apiUrl}users/${userId}/keywords/?event=${eventId}`;
}

export function userInterestsEndpoint(
  userId: number | string,
  eventId: number,
) {
  return (apiUrl: string) =>
    `${apiUrl}users/${userId}/interests/?event=${eventId}`;
}

// Memberships

export function membershipEndpoint(eventId: number | string) {
  if (typeof eventId === 'number') {
    return (apiUrl: string) => `${apiUrl}events/${eventId}/membership/`;
  }
  return (apiUrl: string) => `${apiUrl}events/-/membership/?domain=${eventId}`;
}

export function membershipCalendarSyncStatusEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}memberships/${id}/calendar_sync_status/`;
}

export function membershipGuidanceEndpoint(id: number | string) {
  return (apiUrl: string) => `${apiUrl}memberships/${id}/guidance/`;
}

// Events

export function eventEndpoint(id: number | string) {
  if (typeof id === 'number') {
    return (apiUrl: string) => `${apiUrl}events/${id}/`;
  }
  return (apiUrl: string) => `${apiUrl}events/-/?domain=${id}`;
}

export function groupBraindateTimeSlotsSuggestions(id: number) {
  return (apiUrl: string) =>
    `${apiUrl}events/${id}/group_braindate_timepropositions/`;
}

export function groupBraindateAvailablePeriods(id: number) {
  return (apiUrl: string) =>
    `${apiUrl}events/${id}/group_braindate_timepropositions/?count=1000`;
}

// Topics

export function topicsEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/topics/`;
}

export function topicEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}topics/${id}/`;
}

export function topicConversationEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}topics/${id}/conversation/`;
}

export function keywordsEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/keywords/`;
}

export function featuredKeywordsEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/keywords/?featured=1`;
}

// Braindates

export function braindatesEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/braindates/?user=me`;
}

export function braindateEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}braindates/${id}/`;
}

// Filters

export function filtersEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/filters/`;
}

export function featuredFiltersEndpoint(eventId: number) {
  return (apiUrl: string) => `${apiUrl}events/${eventId}/keywords_categorized/`;
}

// Availability

export function availabilityEndpoint(membershipId: number) {
  return (apiUrl: string) =>
    `${apiUrl}memberships/${membershipId}/availabilities/`;
}

// FAQ

export function faqEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}events/${id}/faq/`;
}

export function faqQuestionEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}faq/questions/${id}/`;
}

// Interests

export function interestsEndpoint(id: number) {
  return (apiUrl: string) => `${apiUrl}events/${id}/interests/`;
}

// Video call feedback

export function feedbackEndpoint(braindateId: number) {
  return (apiUrl: string) =>
    `${apiUrl}plugins/video-call/${braindateId}/feedback/`;
}
